import React, { useState } from "react";
import { ArrowLeft, ArrowRight, User, Hash, IdCard, Calendar } from "lucide-react";

export default function Register({ go, notify, userPhone, onSubmit }) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [fin, setFin] = useState("");
  const [docSeries, setDocSeries] = useState("AA");
  const [docNumber, setDocNumber] = useState("");
  const [birthText, setBirthText] = useState(""); // "DD.MM.YYYY"
  const [gender, setGender] = useState("");

  // Parse DD.MM.YYYY -> {day, month, year, iso, valid, age}
  const parseBirth = (s) => {
    const m = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(s || "");
    if (!m) return { valid: false };
    const day = Number(m[1]);
    const month = Number(m[2]);
    const year = Number(m[3]);
    const d = new Date(year, month - 1, day);
    const valid =
      d.getFullYear() === year &&
      d.getMonth() === month - 1 &&
      d.getDate() === day &&
      year >= 1900 &&
      year <= new Date().getFullYear();
    if (!valid) return { valid: false };
    const ageMs = Date.now() - d.getTime();
    const age = ageMs / (365.25 * 24 * 3600 * 1000);
    const iso = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    return { valid: true, iso, age };
  };

  const birth = parseBirth(birthText);
  const birthOk = birth.valid && birth.age >= 18;

  const handleBirthChange = (raw) => {
    // Keep only digits, auto-insert dots at 2 and 4
    const digits = raw.replace(/\D/g, "").slice(0, 8);
    let out = digits;
    if (digits.length > 4) out = `${digits.slice(0, 2)}.${digits.slice(2, 4)}.${digits.slice(4)}`;
    else if (digits.length > 2) out = `${digits.slice(0, 2)}.${digits.slice(2)}`;
    setBirthText(out);
  };

  const isValid =
    firstName.trim().length >= 2 &&
    lastName.trim().length >= 2 &&
    fin.trim().length === 7 &&
    docSeries.trim().length >= 2 &&
    docNumber.trim().length >= 6 &&
    birthOk &&
    (gender === "male" || gender === "female");

  const handleSubmit = () => {
    if (!isValid) {
      if (birthText && !birth.valid) {
        notify("Doğum tarixini düzgün daxil edin (Gün.Ay.İl)");
        return;
      }
      if (birth.valid && !birthOk) {
        notify("Yaşınız 18-dən aşağı ola bilməz");
        return;
      }
      notify("Zəhmət olmasa bütün sahələri düzgün doldurun");
      return;
    }
    onSubmit({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      birthDate: birth.iso,
      fin: fin.trim().toUpperCase(),
      docSeries: docSeries.trim().toUpperCase(),
      docNumber: docNumber.trim(),
      gender,
      idFront: null,
      idBack: null,
      phone: userPhone,
    });
  };

  return (
    <section className="screen regx-screen" data-testid="register-screen">
      <button
        className="regx-back"
        onClick={() => go("phone")}
        data-testid="register-back-btn"
        aria-label="Geri"
      >
        <ArrowLeft size={20} strokeWidth={2.4} />
      </button>

      {/* Top brand */}
      <div className="regx-brand">
        <div className="regx-brand-row">
          <img src="/assets/logo.png" alt="Pulpay" />
          <span className="regx-brand-name">Pulpay</span>
        </div>
        <span className="regx-brand-sub">Güvənliyiniz bizim üçün dəyərlidir</span>
      </div>

      {/* White card */}
      <div className="regx-card">
        <h2 className="regx-title">Qeydiyyat</h2>
        <p className="regx-subtitle">Hesabınızı yaradın</p>

        <div className="regx-form">
          {/* Ad */}
          <label className="regx-input">
            <span className="regx-input-ic"><User size={18} strokeWidth={2.4} /></span>
            <span className="regx-input-body">
              <input
                type="text"
                placeholder="Ad"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                data-testid="register-firstname"
              />
            </span>
          </label>

          {/* Soyad */}
          <label className="regx-input">
            <span className="regx-input-ic"><User size={18} strokeWidth={2.4} /></span>
            <span className="regx-input-body">
              <input
                type="text"
                placeholder="Soyad"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                data-testid="register-lastname"
              />
            </span>
          </label>

          {/* FIN */}
          <label className="regx-input">
            <span className="regx-input-ic"><Hash size={18} strokeWidth={2.4} /></span>
            <span className="regx-input-body">
              <input
                type="text"
                placeholder="FIN"
                maxLength={7}
                value={fin}
                onChange={(e) =>
                  setFin(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))
                }
                style={{ letterSpacing: 3, textTransform: "uppercase" }}
                data-testid="register-fin"
              />
            </span>
          </label>

          {/* Vəsiqə seriyası — yalnız AA / AZE seçimi */}
          <div className="regx-input regx-input-series">
            <span className="regx-input-ic"><IdCard size={18} strokeWidth={2.4} /></span>
            <div className="regx-input-body">
              <span className="regx-input-label">Şəxsiyyət vəsiqəsinin seriyası</span>
              <div className="regx-series-toggle" role="tablist" data-testid="register-docseries-toggle">
                <button
                  type="button"
                  className={`regx-series-opt ${docSeries === "AA" ? "active" : ""}`}
                  onClick={() => setDocSeries("AA")}
                  data-testid="register-docseries-aa"
                >AA</button>
                <button
                  type="button"
                  className={`regx-series-opt ${docSeries === "AZE" ? "active" : ""}`}
                  onClick={() => setDocSeries("AZE")}
                  data-testid="register-docseries-aze"
                >AZE</button>
              </div>
            </div>
          </div>

          {/* Vəsiqə nömrəsi — ayrı qutu */}
          <label className="regx-input">
            <span className="regx-input-ic"><Hash size={18} strokeWidth={2.4} /></span>
            <span className="regx-input-body">
              <span className="regx-input-label">Şəxsiyyət vəsiqəsinin nömrəsi</span>
              <input
                type="text"
                placeholder="Nömrəni daxil edin"
                maxLength={12}
                value={docNumber}
                onChange={(e) => setDocNumber(e.target.value)}
                data-testid="register-docnumber"
                style={{ letterSpacing: 2, fontWeight: 700 }}
              />
            </span>
          </label>

          {/* Doğum tarixi — DD.MM.YYYY maskalı, hər brauzerdə işləyir */}
          <label className="regx-input">
            <span className="regx-input-ic"><Calendar size={18} strokeWidth={2.4} /></span>
            <span className="regx-input-body">
              <span className="regx-input-label">Doğum tarixi (Gün.Ay.İl)</span>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="bday"
                placeholder="15.08.1995"
                maxLength={10}
                value={birthText}
                onChange={(e) => handleBirthChange(e.target.value)}
                data-testid="register-birthdate"
              />
            </span>
          </label>

          {/* Gender */}
          <div className="regx-gender-label">Cinsiniz</div>
          <div className="regx-gender" data-testid="register-gender">
            <button
              type="button"
              className={`regx-gender-btn ${gender === "male" ? "active" : ""}`}
              onClick={() => setGender("male")}
              data-testid="register-gender-male"
            >
              <span className="regx-gender-ic regx-gender-emoji" aria-hidden="true">👨</span>
              Bəy
            </button>
            <button
              type="button"
              className={`regx-gender-btn ${gender === "female" ? "active" : ""}`}
              onClick={() => setGender("female")}
              data-testid="register-gender-female"
            >
              <span className="regx-gender-ic regx-gender-emoji" aria-hidden="true">👩‍🦰</span>
              Xanım
            </button>
          </div>

          <button
            className="regx-cta"
            onClick={handleSubmit}
            disabled={!isValid}
            data-testid="register-submit-btn"
          >
            Qeydiyyat
            <span className="regx-cta-arrow">
              <ArrowRight size={18} strokeWidth={2.8} />
            </span>
          </button>
        </div>
      </div>
    </section>
  );
}
